/**
 * Shared position-role matching predicates for the multi-position filter
 * (SE-4673/SE-4680). Single source of truth — the Depth panel, Actions
 * Summary, and Transitions card must not re-derive this mapping.
 * @module features/squad-planning/utils/playerMatchesRole
 */

import { POSITION_TO_SLOT_GROUP } from "@/features/squad-planning/constants";
import type { SquadPlayer } from "@/features/squad-planning/types";

/**
 * Whether the player's position abbreviations map onto the given pitch role,
 * either directly or via its POSITION_TO_SLOT_GROUP slot group.
 * @param player - The squad player to test (undefined never matches)
 * @param role - The pitch position role abbreviation (e.g. "LCB")
 * @returns Whether the player is eligible for the role
 */
export function playerMatchesRole(
  player: SquadPlayer | undefined,
  role: string
): boolean {
  if (player == null) return false;
  return player.positionAbbreviations.some(
    abbr => abbr === role || POSITION_TO_SLOT_GROUP[abbr]?.includes(role)
  );
}

/**
 * Union match over the active position filter set: an empty set means "no
 * filter" and matches everything (SE-4680 contract for all three views).
 * @param player - The squad player to test
 * @param roles - The active position role abbreviations
 * @returns Whether the player matches any active role (or the set is empty)
 */
export function playerMatchesAnyRole(
  player: SquadPlayer | undefined,
  roles: readonly string[]
): boolean {
  if (roles.length === 0) return true;
  return roles.some(role => playerMatchesRole(player, role));
}
